"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import { SectionWrapper } from "../components/SectionWraper";

interface Feature6Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  items?: ItemType[];
  attributes?: React.HTMLProps<HTMLElement>;
}

type ItemType = {
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  image?: string;
  imageAltText?: string;
};

const Feature6 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 6",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  items = [],
  attributes,
}: Feature6Props) => {

  const itemBackgroundColors = variantBackgroundColors(darkMode, variant);

  const Item = ({
    title,
    content,
    hasButton,
    buttonText,
    buttonLink,
    buttonStyle,
    image,
    imageAltText,
    className,
  }: ItemType & { className?: string }) => {
    return (
      <Card
        className={cn(
          "p-0 border-none justify-between gap-0",
          itemBackgroundColors,
          className,
        )}
      >
        <CardHeader className="p-6">
          {title && (
            <h6>
              {parse(title)}
            </h6>
          )}
          {content && (
            <div className="paragraph-base text-muted-foreground">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div>
              <Button asChild variant={buttonStyle} size="lg">  
                <Link href={buttonLink ?? ""} className=" justify-start">
                  {buttonText}
                </Link>
              </Button>
            </div>
          )}
        </CardHeader>

        <CardContent
          className="relative flex overflow-hidden p-0"
        >
          <Image
            src={image ?? ""}
            alt={imageAltText ?? ""}
            title={imageAltText ?? ""}
            className="flex-1 rounded-b-xl object-cover aspect-[660/372]"
            width={660}
            height={372}
          />
        </CardContent>
      </Card>
    );
  };

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature6"
      attributes={attributes}
    >
      <div className="container" data-aos="fade-up">
        {title && ( 
          <h2 className="text-center max-w-[500px] lg:max-w-[800px] mx-auto">
            {parse(title)}
          </h2>
        )}
        {content && (
          <div className="paragraph-lg text-muted-foreground text-center max-w-[65ch] mx-auto mt-6">
            {parse(content ?? "")}
          </div>
        )}
        {hasButton && (
          <>
            <div className="flex justify-center mt-10">
              <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
            </div>
            {microCopy && (
              <div className="paragraph-sm text-muted-foreground mt-3 text-center">
                {microCopy}
              </div>
            )}
          </>
        )}
      </div>
      <div className="container" data-aos="fade-up" data-aos-delay="100">
        {items && items.length > 0 && (
          <div className="mt-10 md:mt-20 grid grid-cols-1 gap-4 md:grid-cols-6">
            {items.slice(0, 2).map((item: ItemType, i: number) => {
              const gridClasses = {
                0: "md:col-span-3",
                1: "md:col-span-3 md:col-start-4",
              }[i];
              return <Item key={i} {...item} className={gridClasses} />;
            })}
          </div>
        )}
        {items && items.length > 2 && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-6">
            {items.slice(2, 5).map((item: ItemType, i: number) => {
              const gridClasses = {
                0: "md:col-span-2",
                1: "md:col-span-2 md:col-start-3",
                2: "md:col-span-2 md:col-start-5",
              }[i];
              return <Item key={i + 2} {...item} className={gridClasses} />;
            })}
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export { Feature6 };



